/**
 * Saved views, stored in localStorage — one user's browser, one user's views.
 *
 * The server keyed views by the file's absolute path; a browser-picked file has
 * no path, so its *name* stands in. The file's size and mtime ride along so a
 * view can say "the file changed since this was saved" without pretending to
 * know more than a name can.
 *
 * Same function shapes as the server's views API, so SavedViews runs unchanged.
 */

const KEY = 'csv-explorer.views'
const MAX_VIEWS = 500
const NAME_MAX = 80

function load() {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? '{}')
    return Array.isArray(parsed?.views) ? parsed.views : []
  } catch {
    return []
  }
}

function persist(views) {
  localStorage.setItem(KEY, JSON.stringify({ version: 1, views }))
}

const fail = (message) => Promise.reject(new Error(message))

function cleanName(name, { required = true } = {}) {
  const trimmed = String(name ?? '').trim()
  if (trimmed === '' && required) throw new Error('name is required.')
  if (trimmed.length > NAME_MAX) throw new Error(`name must be ${NAME_MAX} characters or fewer.`)
  return trimmed
}

/** The stored shape, from whatever the caller sent. `name` may be inherited. */
function buildView(input, fallbackName = null) {
  const name = cleanName(input.name, { required: fallbackName === null }) || fallbackName
  return {
    path: String(input.path ?? '').trim(),
    name,
    filters: input.filters ?? { combinator: 'AND', rules: [] },
    dupColumns: input.dupColumns ?? null,
    dupMode: input.dupMode ?? 'duplicates',
    dupResolution: input.dupResolution ?? null,
    hiddenColumns: input.hiddenColumns ?? [],
    customColumns: input.customColumns ?? null,
    insightColumns: input.insightColumns ?? null,
    insightCombine: !!input.insightCombine,
    rowCount: Number.isFinite(input.rowCount) ? input.rowCount : null,
    mtimeMs: Number.isFinite(input.mtimeMs) ? input.mtimeMs : null,
    fileSize: Number.isFinite(input.fileSize) ? input.fileSize : null,
    updatedAt: Date.now(),
  }
}

const sameName = (a, b) => a.toLowerCase() === b.toLowerCase()

export async function listViews(path) {
  const views = load()
  const matching = path ? views.filter((v) => v.path === path) : views
  return { views: [...matching].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0)) }
}

/** Create or overwrite by (path, name) — saving twice with one name updates it. */
export async function saveView(input) {
  const views = load()
  let view
  try {
    view = buildView(input)
  } catch (e) {
    return fail(e.message)
  }
  if (!view.path) return fail('path is required.')

  const existing = views.find((v) => v.path === view.path && sameName(v.name, view.name))
  if (existing) {
    Object.assign(existing, view, { id: existing.id, createdAt: existing.createdAt })
    persist(views)
    return { view: existing }
  }

  if (views.length >= MAX_VIEWS) return fail(`Saved view limit reached (${MAX_VIEWS}).`)
  const created = { id: crypto.randomUUID(), createdAt: Date.now(), ...view }
  views.push(created)
  persist(views)
  return { view: created }
}

/**
 * Overwrite one specific view by id. Unlike saveView this can rename — the name
 * is not the identity here. Omitting `name` keeps the current one.
 */
export async function updateView(id, input) {
  const views = load()
  const existing = views.find((v) => v.id === id)
  if (!existing) return fail('No such saved view.')

  let view
  try {
    view = buildView(input, existing.name)
  } catch (e) {
    return fail(e.message)
  }
  const clash = views.find((v) => v.id !== id && v.path === view.path && sameName(v.name, view.name))
  if (clash) return fail(`Another saved view for this file is already called “${clash.name}”.`)

  Object.assign(existing, view, { id: existing.id, createdAt: existing.createdAt })
  persist(views)
  return { view: existing }
}

export async function deleteView(id) {
  const views = load()
  const next = views.filter((v) => v.id !== id)
  if (next.length === views.length) return fail('No such saved view.')
  persist(next)
  return { deleted: true }
}
